import { useCallback, useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import type { ConfigResponse } from "@site-alerter/shared";
import { getConfig } from "./api.js";
import { Cutoff } from "./components/Cutoff.js";
import { PauseBanner } from "./components/PauseBanner.js";
import { Recipients } from "./components/Recipients.js";
import { SentHistory } from "./components/SentHistory.js";
import { appConfig } from "./env.js";

export function App(): JSX.Element {
  const auth = useAuth();

  if (auth.isLoading) return <Splash>Signing in…</Splash>;

  if (auth.error) {
    return (
      <Splash>
        <p className="error">Sign-in failed: {auth.error.message}</p>
        <button type="button" onClick={() => void auth.signinRedirect()}>
          Try again
        </button>
      </Splash>
    );
  }

  if (!auth.isAuthenticated) {
    return (
      <Splash>
        <h1>Breach Notifier</h1>
        <button type="button" className="primary" onClick={() => void auth.signinRedirect()}>
          Sign in
        </button>
      </Splash>
    );
  }

  return <Dashboard />;
}

function Dashboard(): JSX.Element {
  const auth = useAuth();
  const idToken = auth.user?.id_token ?? "";
  const email = typeof auth.user?.profile.email === "string" ? auth.user.profile.email : "";

  const [config, setConfig] = useState<ConfigResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getConfig(idToken)
      .then((loaded) => {
        if (!cancelled) setConfig(loaded);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(cause instanceof Error ? cause.message : String(cause));
      });
    return () => {
      cancelled = true;
    };
  }, [idToken]);

  /**
   * Clearing local state is not enough: the hosted UI keeps its own session
   * cookie, so the next sign-in would complete silently and the user would
   * appear unable to log out (SPEC §9).
   */
  const signOut = useCallback(() => {
    const params = new URLSearchParams({
      client_id: appConfig.clientId,
      logout_uri: appConfig.redirectUri,
    });
    void auth.removeUser().finally(() => {
      window.location.assign(`${appConfig.cognitoDomain}/logout?${params.toString()}`);
    });
  }, [auth]);

  return (
    <div className="app">
      <header className="topbar">
        <h1>Breach Notifier</h1>
        <div className="who">
          <span>{email}</span>
          <button type="button" onClick={signOut}>
            Sign out
          </button>
        </div>
      </header>

      {error != null && <p className="error banner">{error}</p>}
      {config != null && <PauseBanner config={config} />}

      {config == null && error == null ? (
        <p className="muted">Loading…</p>
      ) : config != null ? (
        <main className="panels">
          <Cutoff idToken={idToken} config={config} onChange={setConfig} />
          <Recipients idToken={idToken} config={config} onChange={setConfig} />
          <SentHistory idToken={idToken} />
        </main>
      ) : null}
    </div>
  );
}

function Splash({ children }: { children: React.ReactNode }): JSX.Element {
  return <div className="splash">{children}</div>;
}
